import React, { useState } from "react";
import { Plus } from "lucide-react";

import MilestoneItem from "../../components/MilestoneItem";
import MilestoneFormModal from "../../components/MilestoneFormModal";
import { deleteMilestone,} from "../../services/milestoneService";
/* ===================================================== */
/* MILESTONES TAB                                        */
/* ===================================================== */

export default function MilestonesTab({
  projectId,
  milestones = [],
  setMilestones,
  isPro = false, // ✅ pass from parent
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ===================================================== */
  /* SAVE (CREATE / UPDATE)                                */
  /* ===================================================== */

  const handleSaved = (saved) => {
    if (!saved) return;

    setMilestones((prev) => {
      const exists = prev.find((m) => m._id === saved._id);

      if (exists) {
        return prev.map((m) =>
          m._id === saved._id ? saved : m
        );
      }

      return [saved, ...prev];
    });

    setModalOpen(false);
    setEditingMilestone(null);
  };

  /* ===================================================== */
  /* DELETE (LOCAL FALLBACK - backend not implemented)     */
  /* ===================================================== */

      const handleDelete = async (milestoneId) => {
        if (!window.confirm(
          "Delete this milestone?"
        )) {
          return;
        }
        setLoading(true);
        setError(null);
        try {
          await deleteMilestone(
            milestoneId
          );
          setMilestones((prev) =>
            prev.filter(
              (m) => m._id !== milestoneId
            )
          );
        } catch (err) {
          setError(
            err?.message ||
            "Failed to delete milestone"
          );
        } finally {
          setLoading(false);
        }
      };

  /* ===================================================== */
  /* OPEN MODAL                                            */
  /* ===================================================== */

  const openCreate = () => {
    setEditingMilestone(null);
    setModalOpen(true);
  };

  const openEdit = (milestone) => {
    setEditingMilestone(milestone);
    setModalOpen(true);
  };

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="space-y-4">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Milestones</h2>
          <p className="text-xs text-gray-500">
            Track key deliverables and investor updates
          </p>
        </div>

        <button
          onClick={openCreate}
          className="flex items-center gap-1 rounded-lg bg-black px-3 py-2 text-sm text-white disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          New Milestone
        </button>
      </div>

  

      {/* ERROR */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* EMPTY */}
      {milestones.length === 0 ? (
        <div className="rounded-xl border bg-white p-6 text-center text-sm text-gray-500">
          No milestones yet. Start by creating one.
        </div>
      ) : (
        <ul className="divide-y rounded-xl border bg-white">
          {milestones.map((m) => (
            <MilestoneItem
              key={m._id}
              milestone={m}
              onEdit={() => openEdit(m)}
              onDelete={() => handleDelete(m._id)} // ✅ added
              isPro={isPro}
            />
          ))}
        </ul>
      )}

      {/* MODAL */}
      <MilestoneFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingMilestone(null);
        }}
        projectId={projectId}
        milestone={editingMilestone}
        onSaved={handleSaved}
        enableShareToggle={isPro} // ✅ gated
        defaultShare={isPro}
        loading={loading}
        setLoading={setLoading}
        setError={setError}
      />
    </div>
  );
}